/**
 * Supabase Client Configuration
 *
 * This file initializes the Supabase client for database operations.
 * Supabase provides a PostgreSQL database with real-time capabilities.
 *
 * Setup Instructions:
 * 1. Create a project at https://supabase.com/dashboard
 * 2. Go to Settings > API
 * 3. Copy the Project URL and anon/public key
 * 4. Add to .env:
 *    REACT_APP_SUPABASE_URL=https://xxxxx.supabase.co
 *    REACT_APP_SUPABASE_ANON_KEY=eyJhbG...
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Initialize Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Check if Supabase is properly configured
 */
export const isSupabaseConfigured = () => {
  return Boolean(supabaseUrl && supabaseAnonKey);
};

/**
 * Get user subscription from database
 * Uses server-side API with JWT authentication
 * @param {string} auth0Id - Auth0 user ID (from user.sub)
 * @param {string} email - User email (fallback lookup)
 * @param {Function} getToken - Async function to get JWT token (getAccessTokenSilently from Auth0)
 * @returns {Promise<Object|null>} Subscription object or null
 */
export const getUserSubscription = async (auth0Id, email = null, getToken = null) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token getter provided
    if (getToken) {
      try {
        const token = await getToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (tokenError) {
        // Token fetch failed - continue without auth for backward compatibility during migration
        console.warn('Failed to get auth token:', tokenError.message);
      }
    }

    const response = await fetch('/api/get-subscription', {
      method: 'POST',
      headers,
      body: JSON.stringify({ auth0Id, email }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Authentication required for subscription check');
      }
      return null;
    }

    const data = await response.json();
    return data.subscription;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    return null;
  }
};

/**
 * Check if user has an active subscription
 * @param {string} auth0Id - Auth0 user ID
 * @param {string} email - User email (fallback lookup)
 * @param {Function} getToken - Async function to get JWT token
 * @returns {Promise<boolean>} True if user has active subscription
 */
export const hasActiveSubscription = async (auth0Id, email = null, getToken = null) => {
  const subscription = await getUserSubscription(auth0Id, email, getToken);

  if (!subscription) {
    return false;
  }

  // Check if subscription is still valid (not expired)
  const now = new Date();
  const endDate = new Date(subscription.current_period_end);

  return subscription.status === 'active' && endDate > now;
};

/**
 * Create or update subscription record
 * @param {Object} subscriptionData - Subscription data from Stripe
 * @returns {Promise<Object>} Created/updated subscription
 */
export const upsertSubscription = async (subscriptionData) => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .upsert(subscriptionData, {
        onConflict: 'stripe_subscription_id'
      })
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error upserting subscription:', error);
    throw error;
  }
};

/**
 * Cancel subscription (mark as cancelled)
 * @param {string} stripeSubscriptionId - Stripe subscription ID
 * @returns {Promise<Object>} Updated subscription
 */
export const cancelSubscription = async (stripeSubscriptionId) => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .update({
        status: 'canceled',
        canceled_at: new Date().toISOString()
      })
      .eq('stripe_subscription_id', stripeSubscriptionId)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

/**
 * Log user signup for tracking
 * Uses server-side API with JWT authentication (RLS compliant)
 * @param {Object} signupData - Signup information
 * @param {Function} getToken - Async function to get JWT token
 * @returns {Promise<Object>} Created signup record
 */
export const logUserSignup = async (signupData, getToken = null) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token getter provided
    if (getToken) {
      try {
        const token = await getToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (tokenError) {
        console.warn('Failed to get auth token:', tokenError.message);
        return null;
      }
    }

    const response = await fetch('/api/user-signup/log', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        auth_method: signupData.auth_method,
        checkout_email: signupData.checkout_email || null,
        stripe_session_id: signupData.stripe_session_id || null,
        subscription_found: signupData.subscription_found || false,
        notes: signupData.notes || null
      }),
    });

    if (!response.ok) {
      console.error('Error logging signup:', response.status);
      return null;
    }

    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error logging signup:', error);
    return null;
  }
};

/**
 * Check if user signup has been logged
 * Uses server-side API with JWT authentication (RLS compliant)
 * @param {string} auth0Id - Auth0 user ID (unused, kept for compatibility)
 * @param {Function} getToken - Async function to get JWT token
 * @returns {Promise<boolean>} True if already logged
 */
export const hasSignupBeenLogged = async (auth0Id, getToken = null) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token getter provided
    if (getToken) {
      try {
        const token = await getToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (tokenError) {
        console.warn('Failed to get auth token:', tokenError.message);
        return false;
      }
    }

    const response = await fetch('/api/user-signup/check', {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      console.error('Error checking signup:', response.status);
      return false;
    }

    const result = await response.json();
    return result.logged || false;
  } catch (error) {
    console.error('Error checking signup log:', error);
    return false;
  }
};

export default supabase;
